import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getMentimeterList, createMentimeter, updateMentimeter, deleteMentimeter,
  updateMentimeterStatus, getQuestions, saveQuestions, getMentimeterResult,
  updateCurrentQ, copyMentimeter, resetMentimeter, uploadMentiImage   // ← 추가
} from '../api/mentimeterApi';
import * as d3 from 'd3';
import cloud from 'd3-cloud';
import { QRCodeCanvas } from 'qrcode.react';
import Toast from '../components/Toast';
import { resolveImageUrl } from '../utils/resolveImageUrl';

const YEAR = new Date().getFullYear().toString();

const getYearOptions = () => {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current + 1; y >= current - 3; y--) years.push(y.toString());
  return years;
};

const Q_TYPES = [
  { value: 'M', label: '객관식' },
  { value: 'T', label: '단답형 (워드클라우드)' },
  { value: 'O', label: 'OX 퀴즈' },
  { value: 'R', label: '순위형' },
  { value: 'S', label: '평점형' },
  { value: 'I', label: '안내(제목+이미지)' },
];
const STATUS_LABEL = { S: '대기', P: '진행중', E: '종료' };
const STATUS_COLOR = { S: '#aaa', P: '#52c41a', E: '#ff4d4f' };

export default function MentimeterPage() {
  const [view, setView]         = useState('list');   // list | form | questions | live
  const [list, setList]         = useState([]);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [selected, setSelected] = useState(null);
  const [form, setForm]         = useState({ title: '' });
  const [questions, setQuestions] = useState([]);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [toast, setToast] = useState('');
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };
  const [qrTarget, setQrTarget] = useState(null);
  const qrCanvasRef = useRef(null)
  const [fullscreen, setFullscreen] = useState(false);

  const loadList = useCallback(async (year = yearFilter) => {
    const res = await getMentimeterList(year);
    if (res.success) setList(res.data);
  }, [yearFilter]);

  useEffect(() => { loadList(yearFilter); }, [yearFilter]);

  // 실시간 폴링
  useEffect(() => {
    if (view !== 'live' || !selected) return;
    const fetch = async () => {
      const res = await getMentimeterResult(selected.menti_year, selected.seq_no);
      if (res.success) setResult(res.data);
    };
    fetch();
    const timer = setInterval(fetch, 2000);
    return () => clearInterval(timer);
  }, [view, selected]);

  // 키보드 방향키 / 프레젠테이션 리모컨 대응 (live 화면에서만 동작)
  useEffect(() => {
    if (view !== 'live') return;

    const handleKeyDown = async (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      const totalQ = result?.result?.length || 0;
      const currentQ = selected?.current_q_no || 0;

      // 다음: → 화살표, PageDown, ↓ 화살표 (리모컨 매핑 대응)
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === 'ArrowDown') {
        e.preventDefault();
        if (currentQ >= totalQ) return;
        const res = await updateCurrentQ(selected.menti_year, selected.seq_no, currentQ + 1);
        if (res.success) setSelected(p => ({ ...p, current_q_no: currentQ + 1 }));
      }
      // 이전: ← 화살표, PageUp, ↑ 화살표 (리모컨 매핑 대응)
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentQ <= 1) return;
        const res = await updateCurrentQ(selected.menti_year, selected.seq_no, currentQ - 1);
        if (res.success) setSelected(p => ({ ...p, current_q_no: currentQ - 1 }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [view, selected, result]); 

  // ==================== 핸들러 ====================

  const handleCreate = () => {
    setSelected(null);
    setForm({ title: '' });
    setError('');
    setView('form');
  };

  const handleEdit = (item) => {
    setSelected(item);
    setForm({ title: item.title });
    setError('');
    setView('form');
  };

  const handleSaveForm = async () => {
    setError('');
    if (!form.title.trim()) { setError('제목을 입력해주세요.'); return; }
    setLoading(true);
    try {
      if (selected) {
        const res = await updateMentimeter(selected.menti_year, selected.seq_no, { title: form.title });
        if (!res.success) { setError(res.message); return; }
      } else {
        const res = await createMentimeter({ menti_year: YEAR, title: form.title });
        if (!res.success) { setError(res.message); return; }
      }
      await loadList();
      setView('list');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    const res = await deleteMentimeter(item.menti_year, item.seq_no);
    if (res.success) loadList();
  };

  const handleCopy = async (item) => {
    if (!window.confirm(`"${item.title}"을 복사하시겠습니까?`)) return;
    const res = await copyMentimeter(item.menti_year, item.seq_no);
    if (res.success) {
      loadList();
      alert('✅ 복사되었습니다.');
    } else {
      alert(`❌ ${res.message}`);
    }
  };

  // QR코드 저장기능 추가
  const handleDownloadQr = (item) => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `멘티미터QR_${item.title}_${item.seq_no}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleReset = async (item) => {
    if (!window.confirm(`"${item.title}"을 초기화하시겠습니까?\n모든 응답이 삭제되고 진행상태가 대기로 돌아갑니다.\n이 작업은 되돌릴 수 없습니다.`)) return;
    const res = await resetMentimeter(item.menti_year, item.seq_no);
    if (res.success) {
      loadList();
      alert('✅ 초기화되었습니다.');
    } else {
      alert(`❌ ${res.message}`);
    }
  };

  const handleStatus = async (item, status) => {
    const res = await updateMentimeterStatus(item.menti_year, item.seq_no, status);
    if (res.success) loadList();
  };

  const handleQuestions = async (item) => {
    setSelected(item);
    const res = await getQuestions(item.menti_year, item.seq_no);
    if (res.success) {
      setQuestions(res.data.map(q => ({
        ...q,
        options: q.options?.map(o => o.opt_name) || [],
        img_preview: resolveImageUrl(q.img_data),   // ← 헬퍼로 변환
      })));
    } else {
      setQuestions([]);
    }
    setError('');
    setView('questions');
  };

  const handleLive = async (item) => {
    // 목록에서 최신 current_q_no를 가져오기 위해 재조회
    const res = await getMentimeterList(YEAR);
    if (res.success) {
      const fresh = res.data.find(i => i.seq_no === item.seq_no && i.menti_year === item.menti_year);
      setSelected(fresh || item);
    } else {
      setSelected(item);
    }
    setResult(null);
    setView('live');
  };

  // ==================== 질문 편집 ====================

  const addQuestion = () => {
    setQuestions(prev => [...prev, { q_type: 'M', q_title: '', options: ['', ''], img_data: null, img_preview: null }]);
  };

  const removeQuestion = (idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const moveQuestion = (idx, direction) => {
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= questions.length) return;
    setQuestions(prev => {
      const next = [...prev];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      return next;
    });
  };

  const updateQ = (idx, key, val) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [key]: val } : q));
  };

  const addOption = (idx) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, options: [...q.options, ''] } : q));
  };

  const removeOption = (idx, oi) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, options: q.options.filter((_, j) => j !== oi) } : q));
  };

  const updateOption = (idx, oi, val) => {
    setQuestions(prev => prev.map((q, i) => i === idx
      ? { ...q, options: q.options.map((o, j) => j === oi ? val : o) }
      : q));
  };

  const handleImgUpload = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { alert('이미지는 3MB 이하만 업로드 가능합니다.'); return; }

    // 업로드 완료 전, 로컬에서 즉시 미리보기
    const localPreview = URL.createObjectURL(file);
    updateQ(idx, 'img_preview', localPreview);
    updateQ(idx, 'img_uploading', true);

    try {
      const res = await uploadMentiImage(selected.menti_year, selected.seq_no, file);
      if (res.success) {
        updateQ(idx, 'img_data', res.url);                    // DB 저장용 — 상대경로 그대로
        updateQ(idx, 'img_preview', resolveImageUrl(res.url)); // 화면 표시용 — 절대경로 보정
        updateQ(idx, 'img_fileName', file.name);
        updateQ(idx, 'img_fileSize', file.size);
      } else {
        alert(`❌ 이미지 업로드 실패: ${res.message}`);
      }
    } catch (err) {
      alert('❌ 이미지 업로드 중 오류가 발생했습니다.');
    } finally {
      updateQ(idx, 'img_uploading', false);
    }
  };

  const handleSaveQuestions = async () => {
    setError('');
    for (const q of questions) {
      if (!q.q_title.trim()) { setError('질문 내용을 모두 입력해주세요.'); return; }
      if (['M', 'R'].includes(q.q_type) && q.options.some(o => !o.trim())) {
        setError('보기 내용을 모두 입력해주세요.'); return;
      }
    }
    setLoading(true);
    try {
      const res = await saveQuestions(selected.menti_year, selected.seq_no, questions);
      if (!res.success) { setError(res.message || '저장에 실패했습니다.'); return; }
      await loadList();
      showToast('✅ 저장되었습니다.');
    } catch (err) {
      if (err.message?.includes('413') || err.message?.toLowerCase().includes('payload')) {
        setError('❌ 업로드 용량이 너무 큽니다. 이미지 크기를 줄여주세요.');
      } else {
        setError('❌ 저장 중 오류가 발생했습니다. 네트워크 상태를 확인해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 링크 복사 기능
  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      alert('✅ 링크가 복사되었습니다!');
    } catch {
      alert('❌ 복사 실패. 직접 복사해주세요:\n' + text);
    }
    document.body.removeChild(textarea);
  };

  // ==================== 렌더 ====================

  if (view === 'form') return (
    <div style={S.wrap}>
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => setView('list')}>← 목록</button>
        <h2 style={S.title}>{selected ? '이벤트 수정' : '이벤트 등록'}</h2>
      </div>
      <div style={S.card}>
        <div style={S.field}>
          <label style={S.label}>제목 *</label>
          <input style={S.input} value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            placeholder="이벤트 제목 입력" />
        </div>
        {error && <div style={S.error}>{error}</div>}
        <button style={{ ...S.btn, ...S.btnPrimary, opacity: loading ? 0.7 : 1 }}
          onClick={handleSaveForm} disabled={loading}>
          {loading ? '저장 중...' : '저장'}
        </button>
      </div>
    </div>
  );

  if (view === 'questions') return (
    <div style={S.wrap}>
      <Toast msg={toast} />
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => setView('list')}>← 목록</button>
        <h2 style={S.title}>질문 편집 — {selected?.title}</h2>
      </div>

      {questions.map((q, idx) => (
        <div key={idx} style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontWeight: 700, color: '#1677ff' }}>질문 {idx + 1}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                style={{ ...S.btn, background: '#f5f5f5', color: '#333', padding: '4px 10px', fontSize: 12, opacity: idx === 0 ? 0.3 : 1, cursor: idx === 0 ? 'default' : 'pointer' }}
                onClick={() => moveQuestion(idx, -1)} disabled={idx === 0}>▲ 위로</button>
              <button
                style={{ ...S.btn, background: '#f5f5f5', color: '#333', padding: '4px 10px', fontSize: 12, opacity: idx === questions.length - 1 ? 0.3 : 1, cursor: idx === questions.length - 1 ? 'default' : 'pointer' }}
                onClick={() => moveQuestion(idx, 1)} disabled={idx === questions.length - 1}>▼ 아래로</button>
              <button style={{ ...S.btn, ...S.btnDanger, padding: '4px 12px', fontSize: 12 }}
                onClick={() => removeQuestion(idx)}>삭제</button>
            </div>
          </div>

          {/* 유형 */}
          <div style={S.field}>
            <label style={S.label}>유형</label>
            <select style={S.input} value={q.q_type}
              onChange={e => updateQ(idx, 'q_type', e.target.value)}>
              {Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {/* 질문 내용 */}
          <div style={S.field}>
            <label style={S.label}>질문 내용 *</label>
            <input style={S.input} value={q.q_title}
              onChange={e => updateQ(idx, 'q_title', e.target.value)}
              placeholder="질문을 입력하세요" />
          </div>

          {/* 이미지 */}
          <div style={S.field}>
            <label style={S.label}>이미지 (선택, 3MB 이하)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label htmlFor={`menti-img-${idx}`}
                style={{ background: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', padding: '6px 14px', fontSize: 13, fontWeight: 600 }}>
                파일 선택
              </label>
              <input id={`menti-img-${idx}`} type="file" accept="image/*"
                style={{ display: 'none' }} onChange={e => handleImgUpload(idx, e)} />
              <span style={{ fontSize: 13, color: '#888' }}>
                {q.img_uploading ? '업로드 중...' : q.img_fileName
                  ? `${q.img_fileName} (${(q.img_fileSize / 1024).toFixed(1)}KB)`
                  : q.img_preview ? '기존 이미지 있음' : '선택된 파일 없음'}
              </span>
            </div>
            {q.img_preview && (
              <div style={{ marginTop: 8 }}>
                <img src={q.img_preview} alt="preview"
                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #eee' }} />
                <button style={{ ...S.btn, ...S.btnDanger, marginTop: 4, padding: '2px 10px', fontSize: 12 }}
                  onClick={() => {
                    updateQ(idx, 'img_data', null);
                    updateQ(idx, 'img_preview', null);
                    updateQ(idx, 'img_fileName', null);
                    updateQ(idx, 'img_fileSize', null);
                  }}>
                  이미지 삭제
                </button>
              </div>
            )}
          </div>

          {/* 객관식/순위형 보기 */}
          {['M', 'R'].includes(q.q_type) && (
            <div style={S.field}>
              <label style={S.label}>보기</label>
              {q.options.map((o, oi) => (
                <div key={oi} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input style={{ ...S.input, flex: 1 }} value={o}
                    onChange={e => updateOption(idx, oi, e.target.value)}
                    placeholder={`보기 ${oi + 1}`} />
                  {q.options.length > 2 && (
                    <button style={{ ...S.btn, ...S.btnDanger, padding: '4px 10px' }}
                      onClick={() => removeOption(idx, oi)}>✕</button>
                  )}
                </div>
              ))}
              <button style={{ ...S.btn, background: '#f0f0f0', color: '#333', marginTop: 4 }}
                onClick={() => addOption(idx)}>+ 보기 추가</button>
            </div>
          )}
        </div>
      ))}

      <button style={{ ...S.btn, background: '#f6ffed', color: '#52c41a', border: '1px dashed #52c41a', width: '100%', marginBottom: 16 }}
        onClick={addQuestion}>+ 질문 추가</button>

      {error && <div style={S.error}>{error}</div>}
      <button style={{ ...S.btn, ...S.btnPrimary, width: '100%', opacity: loading ? 0.7 : 1 }}
        onClick={handleSaveQuestions} disabled={loading}>
        {loading ? '저장 중...' : '질문 저장'}
      </button>
    </div>
  );

  if (view === 'live') {
    const totalQ = result?.result?.length || 0;
    const currentQ = selected?.current_q_no || 0;
    const currentResult = result?.result?.find(q => q.q_no === currentQ);

    // 질문 번호 생성용 핸들러
    const getDisplayQNo = (q) => {
      if (!result?.result) return q.q_no;
      const answerableList = result.result.filter(r => r.q_type !== 'I');
      const idx = answerableList.findIndex(r => r.q_no === q.q_no);
      return idx + 1;
    };

    // 진행률 산출용 핸들러
    const getProgressLabel = () => {
      if (!result?.result) return `${currentQ} / ${totalQ}`;
      const answerableList = result.result.filter(r => r.q_type !== 'I');
      const totalReal = answerableList.length;
      if (!currentResult) return `0 / ${totalReal}`;
      if (currentResult.q_type === 'I') return `전체 ${totalReal}문항`;
      const idx = answerableList.findIndex(r => r.q_no === currentResult.q_no);
      return `${idx + 1} / ${totalReal}`;
    };

    const handlePrevQ = async () => {
      if (currentQ <= 1) return;
      const res = await updateCurrentQ(selected.menti_year, selected.seq_no, currentQ - 1);
      if (res.success) setSelected(p => ({ ...p, current_q_no: currentQ - 1 }));
    };

    const handleNextQ = async () => {
      if (currentQ >= totalQ) return;
      const res = await updateCurrentQ(selected.menti_year, selected.seq_no, currentQ + 1);
      if (res.success) setSelected(p => ({ ...p, current_q_no: currentQ + 1 }));
    };

    const handleStartQ = async () => {
      const res = await updateCurrentQ(selected.menti_year, selected.seq_no, 1);
      if (res.success) setSelected(p => ({ ...p, current_q_no: 1 }));
    };

    if (fullscreen && currentResult) return (
      <div style={{ position: 'fixed', inset: 0, background: '#ffffff', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
        {/* 상단 바 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
          <div style={{ color: '#1a1a2e', fontWeight: 800, fontSize: 36 }}>
            {currentResult.q_type === 'I' ? currentResult.q_title : `Q${getDisplayQNo(currentResult)}. ${currentResult.q_title}`}
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ color: '#888', fontSize: 14 }}>
              {currentResult?.answerCount ?? 0}명 응답
            </span>
            <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
              onClick={handlePrevQ} disabled={currentQ <= 1}>◀ 이전</button>
            <span style={{ fontSize: 13, color: '#888' }}>{getProgressLabel()}</span>
            <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
              onClick={handleNextQ} disabled={currentQ >= totalQ}>다음 ▶</button>
            <div style={{ width: 50 }} />  {/* ← 추가: "다음" 버튼과 "나가기" 사이 여백 */}
            <button style={{ ...S.btn, background: '#ff4d4f', color: '#fff' }}
              onClick={() => setFullscreen(false)}>✕ 나가기</button>
          </div>
        </div>
        {/* 결과 차트 */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 40 }}>
          {currentResult.q_type === 'I' ? (
            <div style={{ textAlign: 'center' }}>
              {currentResult.img_data && (
                <img src={resolveImageUrl(currentResult.img_data)} alt="질문 이미지"
                  style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: 12 }} />
              )}
            </div>
          ) : (
            <>
              <div style={{ width: '100%', maxWidth: currentResult.img_data ? 700 : 900 }}>
                <ResultChartFullscreen q={currentResult} />
              </div>
              {currentResult.img_data && (
                <div style={{ flex: '0 0 auto', maxWidth: 400 }}>
                  <img src={resolveImageUrl(currentResult.img_data)} alt="질문 이미지"
                    style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 12 }} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );

    return (
      <div style={S.wrap}>
        <div style={S.header}>
          <button style={S.backBtn} onClick={() => setView('list')}>← 목록</button>
          <h2 style={S.title}>📊 실시간 결과 — {selected?.title}</h2>
        </div>

        {/* 응답자 수 + 질문 컨트롤 */}
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <span style={{ fontSize: 24, fontWeight: 800, color: '#1677ff' }}>
                {currentResult?.answerCount ?? 0}
              </span>
              <span style={{ fontSize: 14, color: '#888', marginLeft: 8 }}>명 응답</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {currentQ === 0 ? (
                <button style={{ ...S.btn, background: '#52c41a', color: '#fff' }}
                  onClick={handleStartQ}>▶ 질문 시작</button>
              ) : (
                <>
                  <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
                    onClick={handlePrevQ} disabled={currentQ <= 1}>◀ 이전</button>
                  <span style={{ color: '#888', fontSize: 13 }}>{getProgressLabel()}</span>
                  <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
                    onClick={handleNextQ} disabled={currentQ >= totalQ}>다음 ▶</button>
                </>
              )}
              <div style={{ width: 50 }} />  {/* ← 추가: "다음" 버튼과 "나가기" 사이 여백 */}
              <button style={{ ...S.btn, ...S.btnPrimary, opacity: currentQ === 0 ? 0.5 : 1, cursor: currentQ === 0 ? 'not-allowed' : 'pointer' }}
                onClick={() => setFullscreen(true)} disabled={currentQ === 0}>⛶ 전체화면</button>
            </div>
          </div>
        </div>

        {/* 현재 질문 결과 */}
        {currentResult ? (
          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 30 }}>
              {currentResult.q_type === 'I' ? currentResult.q_title : `Q${getDisplayQNo(currentResult)}. ${currentResult.q_title}`}
            </div>
            {currentResult.q_type === 'I' ? (
              <div style={{ textAlign: 'center' }}>
                {currentResult.img_data && (
                  <img src={resolveImageUrl(currentResult.img_data)} alt="질문 이미지"
                    style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', borderRadius: 8 }} />
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                  <ResultChart q={currentResult} />
                </div>
                {currentResult.img_data && (
                  <div style={{ flex: '0 0 auto', maxWidth: 320 }}>
                    <img src={resolveImageUrl(currentResult.img_data)} alt="질문 이미지"
                      style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 8 }} />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>
            {currentQ === 0 ? '질문 시작 버튼을 눌러주세요.' : '데이터 로딩 중...'}
          </div>
        )}
      </div>
    );
  }

  // 목록
  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h2 style={S.title}>📊 생각나눔</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select style={S.yearSelect} value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
            {getYearOptions().map(y => (
              <option key={y} value={y}>{y}년</option>
            ))}
          </select>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={handleCreate}>+ 등록</button>
        </div>
      </div>

      {list.length === 0 && (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 60 }}>등록된 이벤트가 없습니다.</div>
      )}

      {list.map(item => (
        <div key={item.seq_no} style={{ ...S.card, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{item.title}</span>
              <span style={{
                marginLeft: 10, fontSize: 12, fontWeight: 600,
                color: STATUS_COLOR[item.status]
              }}>● {STATUS_LABEL[item.status]}</span>
            </div>
            <span style={{ fontSize: 12, color: '#aaa' }}>{item.reg_dt?.slice(0, 10)}</span>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {/* 상태 버튼 */}
            {item.status === 'S' && (
              <button style={{ ...S.btn, background: '#52c41a', color: '#fff' }}
                onClick={() => handleStatus(item, 'P')}>▶ 시작</button>
            )}
            {item.status === 'P' && (
              <button style={{ ...S.btn, background: '#ff4d4f', color: '#fff' }}
                onClick={() => handleStatus(item, 'E')}>■ 종료</button>
            )}
            {item.status === 'P' && (
              <button style={{ ...S.btn, ...S.btnPrimary }}
                onClick={() => handleLive(item)}>📊 실시간</button>
            )}
            {item.status === 'E' && (
              <button style={{ ...S.btn, ...S.btnPrimary }}
                onClick={() => handleLive(item)}>📊 결과보기</button>
            )}
            {/* 편집 버튼 */}
            {item.status === 'S' && (
              <>
                <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
                  onClick={() => handleQuestions(item)}>질문편집</button>
                <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
                  onClick={() => handleEdit(item)}>제목수정</button>
                <button style={{ ...S.btn, ...S.btnDanger }}
                  onClick={() => handleDelete(item)}>삭제</button>
              </>
            )}
            {/* 복사 - 모든 상태에서 가능 */}
            <button style={{ ...S.btn, background: '#f9f0ff', color: '#722ed1', border: '1px solid #d3adf7' }}
              onClick={() => handleCopy(item)}>📑 복사</button>
            {/* 초기화 - 진행중/종료 상태(응답이 쌓인 경우)에서만 의미있음 */}
            {(item.status === 'P' || item.status === 'E') && (
              <button style={{ ...S.btn, background: '#fff7e6', color: '#fa8c16', border: '1px solid #ffd591' }}
                onClick={() => handleReset(item)}>🔄 초기화</button>
            )}
            {/* QR 버튼 - 모든 상태에서 표시 */}
            <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
              onClick={() => {
                const url = `${window.location.origin}/?page=mentimeter&branch=${item.branch_code}&division=${item.division_code}&year=${item.menti_year}&seq=${item.seq_no}`;
                
                // HTTPS: clipboard API 사용, HTTP: execCommand fallback
                if (navigator.clipboard && window.isSecureContext) {
                  navigator.clipboard.writeText(url)
                    .then(() => alert('✅ 링크가 복사되었습니다!'))
                    .catch(() => fallbackCopy(url));
                } else {
                  fallbackCopy(url);
                }
              }}>
              📋 링크복사
            </button>
            <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
              onClick={() => setQrTarget(qrTarget?.seq_no === item.seq_no ? null : item)}>
              🔳 QR
            </button>
          </div>

          {/* QR코드 펼치기 */}
          {qrTarget?.seq_no === item.seq_no && (
            <div style={{ marginTop: 12, textAlign: 'center', padding: '16px 0', borderTop: '1px solid #f0f0f0' }}>
              <div ref={qrCanvasRef} style={{ display: 'flex', justifyContent: 'center' }}>
                <QRCodeCanvas
                  value={`${window.location.origin}/?page=mentimeter&branch=${item.branch_code}&division=${item.division_code}&year=${item.menti_year}&seq=${item.seq_no}`}
                  size={160}
                />
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 8, wordBreak: 'break-all' }}>
                {`${window.location.origin}/?page=mentimeter&branch=${item.branch_code}&division=${item.division_code}&year=${item.menti_year}&seq=${item.seq_no}`}
              </div>
              <button style={{ ...S.btn, background: '#1677ff', color: '#fff', marginTop: 10, padding: '6px 16px', fontSize: 12 }}
                onClick={() => handleDownloadQr(item)}>
                💾 저장
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ==================== 결과 차트 컴포넌트 ====================

function ResultChart({ q }) {
  if (q.q_type === 'I') {
    return (
      <div style={{ textAlign: 'center', color: '#aaa', padding: '20px 0' }}>
        📢 안내 슬라이드입니다.
      </div>
    );
  }

  if (q.q_type === 'T') {
    const words = q.answers.map(a => ({ text: a.answer, value: Number(a.cnt) }));
    if (words.length === 0) return <div style={{ color: '#aaa', textAlign: 'center' }}>응답 없음</div>;
    return <div style={{ minHeight: 300 }}>
              <WordCloud words={words} />
           </div>;
  }

  if (q.q_type === 'O') {
    // OX
    const o = q.answers.find(a => a.answer === 'O')?.cnt || 0;
    const x = q.answers.find(a => a.answer === 'X')?.cnt || 0;
    const total = Number(o) + Number(x);
    return (
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
        {[{ label: 'O', cnt: o, color: '#52c41a' }, { label: 'X', cnt: x, color: '#ff4d4f' }].map(item => (
          <div key={item.label} style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: 48 }}>{item.label === 'O' ? '⭕' : '❌'}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: item.color }}>{item.cnt}표</div>
            <div style={{ fontSize: 13, color: '#888' }}>
              {total > 0 ? Math.round(item.cnt / total * 100) : 0}%
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (q.q_type === 'S') {
    // 평점
    const total = q.answers.reduce((s, a) => s + Number(a.cnt), 0);
    const sum = q.answers.reduce((s, a) => s + Number(a.answer) * Number(a.cnt), 0);
    const avg = total > 0 ? (sum / total).toFixed(1) : 0;
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40, fontWeight: 800, color: '#faad14' }}>⭐ {avg}</div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>평균 ({total}명 응답)</div>
        <div style={{ marginTop: 12 }}>
          {[5, 4, 3, 2, 1].map(star => {
            const cnt = q.answers.find(a => Number(a.answer) === star)?.cnt || 0;
            const pct = total > 0 ? Math.round(cnt / total * 100) : 0;
            return (
              <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ width: 24, fontSize: 12 }}>{star}★</span>
                <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 16 }}>
                  <div style={{ width: `${pct}%`, background: '#faad14', height: '100%', borderRadius: 4, transition: 'width 0.5s' }} />
                </div>
                <span style={{ width: 36, fontSize: 12, color: '#888', textAlign: 'right' }}>{cnt}표</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 객관식 / 순위형 → 막대 그래프
  const total = q.answers.reduce((s, a) => s + Number(a.cnt), 0);
  if (q.answers.length === 0) return <div style={{ color: '#aaa', textAlign: 'center' }}>응답 없음</div>;
  return (
    <div>
      {q.answers.map((a, i) => {
        const pct = total > 0 ? Math.round(Number(a.cnt) / total * 100) : 0;
        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span>{a.answer}</span>
              <span style={{ color: '#888' }}>{a.cnt}표 ({pct}%)</span>
            </div>
            <div style={{ background: '#f0f0f0', borderRadius: 4, height: 20 }}>
              <div style={{ width: `${pct}%`, background: '#1677ff', height: '100%', borderRadius: 4, transition: 'width 0.5s' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ResultChartFullscreen({ q }) {
  if (q.q_type === 'I') {
    return (
      <div style={{ textAlign: 'center', color: '#aaa', fontSize: 28 }}>
        📢 안내 슬라이드입니다.
      </div>
    );
  }

  if (q.q_type === 'T') {
    const words = q.answers.map(a => ({ text: a.answer, value: Number(a.cnt) }));
    if (words.length === 0) return <div style={{ color: '#aaa', textAlign: 'center', fontSize: 24 }}>응답 없음</div>;
    return (
      <div style={{ minHeight: 300 }}>
        <WordCloud words={words} />
      </div>
    );
  }

  if (q.q_type === 'O') {
    const o = q.answers.find(a => a.answer === 'O')?.cnt || 0;
    const x = q.answers.find(a => a.answer === 'X')?.cnt || 0;
    const total = Number(o) + Number(x);
    return (
      <div style={{ display: 'flex', gap: 40, justifyContent: 'center' }}>
        {[{ label: 'O', cnt: o, color: '#52c41a' }, { label: 'X', cnt: x, color: '#ff4d4f' }].map(item => (
          <div key={item.label} style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: 120 }}>{item.label === 'O' ? '⭕' : '❌'}</div>
            <div style={{ fontSize: 72, fontWeight: 800, color: item.color }}>{item.cnt}표</div>
            <div style={{ fontSize: 28, color: '#888' }}>
              {total > 0 ? Math.round(item.cnt / total * 100) : 0}%
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (q.q_type === 'S') {
    const total = q.answers.reduce((s, a) => s + Number(a.cnt), 0);
    const sum = q.answers.reduce((s, a) => s + Number(a.answer) * Number(a.cnt), 0);
    const avg = total > 0 ? (sum / total).toFixed(1) : 0;
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 100, fontWeight: 800, color: '#faad14' }}>⭐ {avg}</div>
        <div style={{ fontSize: 20, color: '#888', marginTop: 8 }}>평균 ({total}명 응답)</div>
        <div style={{ marginTop: 24, maxWidth: 500, margin: '24px auto 0' }}>
          {[5, 4, 3, 2, 1].map(star => {
            const cnt = q.answers.find(a => Number(a.answer) === star)?.cnt || 0;
            const pct = total > 0 ? Math.round(cnt / total * 100) : 0;
            return (
              <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ width: 40, fontSize: 18, color: '#333' }}>{star}★</span>
                <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 6, height: 28 }}>
                  <div style={{ width: `${pct}%`, background: '#faad14', height: '100%', borderRadius: 6, transition: 'width 0.5s' }} />
                </div>
                <span style={{ width: 60, fontSize: 16, color: '#888', textAlign: 'right' }}>{cnt}표</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 객관식 / 순위형
  const total = q.answers.reduce((s, a) => s + Number(a.cnt), 0);
  if (q.answers.length === 0) return <div style={{ color: '#aaa', textAlign: 'center', fontSize: 24 }}>응답 없음</div>;
  return (
    <div>
      {q.answers.map((a, i) => {
        const pct = total > 0 ? Math.round(Number(a.cnt) / total * 100) : 0;
        return (
          <div key={i} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 20, marginBottom: 8, color: '#1a1a2e' }}>
              <span>{a.answer}</span>
              <span style={{ color: '#888' }}>{a.cnt}표 ({pct}%)</span>
            </div>
            <div style={{ background: '#f0f0f0', borderRadius: 8, height: 48 }}>
              <div style={{ width: `${pct}%`, background: 'linear-gradient(135deg, #1677ff, #4096ff)', height: '100%', borderRadius: 8, transition: 'width 0.5s' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WordCloud({ words }) {
  const svgRef = useRef(null);
  const prevKeyRef = useRef('');

  // words의 실제 내용(텍스트+빈도)을 키로 변환 — 참조가 달라도 내용이 같으면 동일한 키
  const wordsKey = words.map(w => `${w.text}:${w.value}`).sort().join('|');

  useEffect(() => {
    if (!svgRef.current || words.length === 0) return;

    // 내용이 이전과 동일하면 재계산 스킵 (위치 고정)
    if (wordsKey === prevKeyRef.current) return;
    prevKeyRef.current = wordsKey;

    console.log('전체 단어 개수:', words.length, words.map(w => w.text));

    const width = svgRef.current.clientWidth || 600;
    const height = Math.min(600, Math.max(300, words.length * 25));
    
    const maxVal = Math.max(...words.map(w => w.value));
    const minVal = Math.min(...words.map(w => w.value));
    const fontSize = d3.scaleSqrt()
      .domain([minVal, maxVal])
      .range([30, 120]);

    // 총 24가지 색상으로 설정
    const colors = ['#1677ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2', 
                    '#06449b', '#379d03', '#8e5f02', '#d33133', '#5e27aa', '#129d9d', 
                    '#010aaf', '#216100', '#ba7e06', '#bd2b2d', '#461d80', '#096666', 
                    '#001d45', '#1b5001', '#765003', '#970407', '#29104c', '#053939', ];

    d3.select(svgRef.current).selectAll('*').remove();

    cloud()
      .size([width, height])
      .words(
        [...words]
          .sort((a, b) => b.value - a.value)  // 빈도 높은 순으로 정렬 → 가장 큰 단어가 먼저 중앙에 배치됨
          .map(w => ({ text: w.text, size: fontSize(w.value) }))
      )
      .padding(8)
      .rotate(0)
      .font('Noto Sans KR')
      .fontSize(d => d.size)
      .on('end', (laid) => {
        d3.select(svgRef.current)
          .attr('width', width)
          .attr('height', height)
          .append('g')
          .attr('transform', `translate(${width / 2},${height / 2})`)
          .selectAll('text')
          .data(laid)
          .enter().append('text')
          .style('font-size', d => `${d.size}px`)
          .style('font-family', 'Noto Sans KR, sans-serif')
          .style('font-weight', '700')
          .style('fill', (_, i) => colors[i % colors.length])
          .attr('text-anchor', 'middle')
          .attr('transform', d => `translate(${d.x},${d.y})`)
          .text(d => d.text);
      })
      .start();
  }, [wordsKey]);

  //return <svg ref={svgRef} style={{ width: '100%', height: 'auto', display: 'block', background: '#f5f5f5' }} />;
  return <svg ref={svgRef} style={{ width: '100%', height: 'auto', display: 'block' }} />;
}

const S = {
  wrap    : { padding: 16, maxWidth: 1200, margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  header  : { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title   : { fontSize: 20, fontWeight: 800, margin: 0 },
  card    : { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  field   : { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 },
  label   : { fontSize: 13, fontWeight: 600, color: '#444' },
  input   : { border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', background: '#fafafa' },
  btn     : { border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnPrimary : { background: '#1677ff', color: '#fff' },
  btnDanger  : { background: '#fff1f0', color: '#ff4d4f', border: '1px solid #ffccc7' },
  backBtn : { background: 'none', border: 'none', fontSize: 14, color: '#1677ff', cursor: 'pointer', padding: 0 },
  error   : { background: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#c0392b', marginBottom: 12 },
  yearSelect : { border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', background: '#fff', cursor: 'pointer' },
};